'use client'

import React from 'react'
import { ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { TypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import {
  PageTileSectionTileTextAlignment,
  PageTileSectionTileTextPlacement,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { Tile as BaseTile } from '../../../tiles/Tiles.client'
import { ContentfulStyledLink } from '../components/ContentfulStyledLink'

import { SectionAlignment } from './ContentfulPageTileSection.client'

type TitleBaseProps = {
  $textAlign?: string
  $mobileTextAlign?: string
  $hasHorizontalMargin?: boolean
}

type StyledButtonRowProps = {
  $textAlignment: 'left' | 'center' | 'right'
}

const TitleBase = css<TitleBaseProps>`
  margin-bottom: 16px;
  padding: 0 ${o => o.$hasHorizontalMargin && o.theme.dimensions.layout.sidePadding.md + 'px'};
  text-align: ${props => props.$mobileTextAlign ?? 'left'};

  ${fromMd} {
    text-align: ${props => props.$textAlign ?? 'left'};
    margin-bottom: 24px;
  }
`
const TitleDefault = styled(TypeStyle.HeadlineSmall)<TitleBaseProps>`
  ${TitleBase}
`

const TitleMedium = styled(TypeStyle.HeadlineMedium)<TitleBaseProps>`
  ${TitleBase}
`

const TitleLarge = styled(TypeStyle.HeadlineLarge)<TitleBaseProps>`
  ${TitleBase}
`

const TitleExtraLarge = styled(TypeStyle.HeadlineExtraLarge)<TitleBaseProps>`
  ${TitleBase}
`
export const PageTileSectionTitle: React.FC<
  {
    $titleTextSize: 'h1' | 'h2' | 'h3' | 'h4'
    children: string | ReactNode
  } & TitleBaseProps
> = ({ $titleTextSize, ...props }) => {
  switch ($titleTextSize) {
    case 'h1':
      return (
        <TitleExtraLarge asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleExtraLarge>
      )
    case 'h2':
      return (
        <TitleLarge asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleLarge>
      )
    case 'h3':
      return (
        <TitleMedium asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleMedium>
      )
    default:
      return (
        <TitleDefault asTag={$titleTextSize} {...props}>
          {props.children}
        </TitleDefault>
      )
  }
}

export const Container = styled.div<{
  $textAlign: SectionAlignment
  $hasHover: boolean
  $hasVerticalMargin?: boolean
  $hasOverlay?: boolean
  $shouldUseCollectionStyling?: boolean
  $aspectRatios: { sm: number; md: number }
  $isTwoUp?: boolean
  $hasContentBelow?: boolean
  $roundCorners: { sm: boolean; md: boolean }
}>`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  text-align: ${o => o.$textAlign};

  & > img,
  & > video {
    width: 100%;
    object-fit: cover;
    font-family: 'object-fit: cover;';
    max-height: 350px;
    aspect-ratio: ${o => o.$aspectRatios.sm};
  }

  ${fromMd} {
    border-radius: ${o => o.$roundCorners.md && '6px'};
    & > img,
    & > video {
      max-height: 650px;
      max-width: 740px;
      aspect-ratio: ${o => o.$aspectRatios.md};
    }
  }

  ${o =>
    o.$hasHover &&
    `
      &:hover ${Image},
      {
        display: none;
      }
      `}
`

export const TileOverlay = styled.div<{
  $placement: PageTileSectionTileTextPlacement | null
  $textAlignment: PageTileSectionTileTextAlignment | null
  $hexOverlayOpacity: string | null
}>`
  box-sizing: border-box;
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: ${o => o.theme.spacing(5)};

  align-items: ${o =>
    o.$textAlignment === 'center'
      ? 'center'
      : o.$textAlignment === 'right'
      ? 'flex-end'
      : 'flex-start'};

  ${o =>
    o.$hexOverlayOpacity
      ? `background-color: ${o.theme.color.fill.background + o.$hexOverlayOpacity};`
      : null}

  justify-content: ${o => {
    switch (o.$placement) {
      case 'overlayTop':
        return 'flex-start'
      case 'overlayCenter':
        return 'center'
      case 'overlayBottom':
        return 'flex-end'
      default:
        return 'flex-end'
    }
  }};

  ${fromMd} {
    padding: ${o => o.theme.spacing(10)};
  }
`

export const TileOverlayContent = styled.div<{
  $alignment: PageTileSectionTileTextAlignment
}>`
  text-align: ${o => o.$alignment};
  box-sizing: border-box;
  max-width: 650px;
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
`

export const TileTextBelow = styled.div<{
  $textAlignment: PageTileSectionTileTextAlignment
  $isTwoUp?: boolean
}>`
  color: ${o => o.theme.color.text.primary.on.background};
  align-items: ${o =>
    o.$textAlignment === 'center'
      ? 'center'
      : o.$textAlignment === 'right'
      ? 'flex-end'
      : 'flex-start'};

  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2.5)};
  margin-top: ${o => o.theme.spacing(4)};
  ${o => o.$isTwoUp && `margin: ${o.theme.spacing(5)};`}

  ${fromMd} {
    margin: ${o => o.$isTwoUp && o.theme.spacing(10)};
  }
`

export const StyledContentfulStyledLink = styled(ContentfulStyledLink)<{ $isButtonLink?: boolean }>`
  display: inline-flex;
  text-transform: uppercase;
  ${o => o.theme.typography.effect.uppercase};
  ${untilMd} {
    ${o => o.$isButtonLink && `padding: 0 ${o.theme.spacing(2)};`}
  }
`
export const Tile = styled(BaseTile)`
  display: flex;
  position: relative;
  min-width: 204px;
  max-width: 740px;
`

export const StyledButtonRow = styled.div<StyledButtonRowProps>`
  display: flex;
  justify-content: ${o =>
    o.$textAlignment === 'center'
      ? 'center'
      : o.$textAlignment === 'right'
      ? 'flex-end'
      : 'flex-start'};
`

export const StyledTileTextContent = styled.div<{ $isOverlay: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
  color: ${o => o.$isOverlay && o.theme.color.text.highContrast.on.subtle};
`
